import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X } from "lucide-react";
import { useLanguage } from "../i18n/LanguageContext";
import { languageNames } from "../i18n/translations";
import { Logo } from "./Logo";

const scrollTo = (id) => {
  const el = document.getElementById(id);
  if (el) el.scrollIntoView({ behavior: "smooth" });
};

export const Navbar = () => {
  const { lang, changeLang, t } = useLanguage();
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const links = [
    { id: "fleet", label: t.nav.fleet, testid: "nav-fleet-link" },
    { id: "destinations", label: t.nav.destinations, testid: "nav-destinations-link" },
    { id: "philosophy", label: t.nav.philosophy, testid: "nav-philosophy-link" },
    { id: "contact", label: t.nav.contact, testid: "nav-contact-link" },
  ];

  const go = (id) => {
    setOpen(false);
    setTimeout(() => scrollTo(id), open ? 150 : 0);
  };

  return (
    <>
      <motion.header
        initial={{ y: -80, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1], delay: 0.4 }}
        className={`fixed top-0 inset-x-0 z-50 transition-all duration-500 ${
          scrolled ? "bg-[#050B14]/85 backdrop-blur-xl border-b border-[rgba(212,175,55,0.15)]" : "bg-transparent"
        }`}
        data-testid="navbar"
      >
        <div className="max-w-7xl mx-auto px-6 lg:px-10 h-20 flex items-center justify-between">
          <button onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })} aria-label="Malta Adria home" data-testid="nav-home-logo">
            <Logo compact />
          </button>

          <nav className="hidden lg:flex items-center gap-10">
            {links.map((l) => (
              <button
                key={l.id}
                onClick={() => go(l.id)}
                data-testid={l.testid}
                className="gold-underline-link font-label text-[11px] tracking-[0.3em] uppercase text-slate-300 hover:text-[#F3E5AB] transition-colors duration-300"
              >
                {l.label}
              </button>
            ))}
          </nav>

          <div className="hidden lg:flex items-center gap-6">
            <div className="flex items-center gap-1 border border-[rgba(212,175,55,0.25)] rounded-full px-1 py-1" data-testid="language-switcher">
              {Object.keys(languageNames).map((code) => (
                <button
                  key={code}
                  onClick={() => changeLang(code)}
                  data-testid={`lang-switch-${code}`}
                  className={`font-label text-[10px] tracking-[0.15em] px-2.5 py-1 rounded-full transition-all duration-300 ${
                    lang === code ? "bg-[#D4AF37] text-[#050B14] font-semibold" : "text-slate-400 hover:text-[#F3E5AB]"
                  }`}
                >
                  {languageNames[code]}
                </button>
              ))}
            </div>
            <button
              onClick={() => go("contact")}
              data-testid="nav-enquire-button"
              className="font-label text-[11px] tracking-[0.25em] uppercase px-6 py-2.5 border border-[#D4AF37] text-[#D4AF37] hover:bg-[#D4AF37] hover:text-[#050B14] transition-all duration-300"
            >
              {t.nav.enquire}
            </button>
          </div>

          <button
            className="lg:hidden text-[#F7F5F0]"
            onClick={() => setOpen(true)}
            aria-label="Open menu"
            data-testid="mobile-menu-button"
          >
            <Menu size={26} />
          </button>
        </div>
      </motion.header>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.35 }}
            className="fixed inset-0 z-[70] bg-[#050B14]/97 backdrop-blur-2xl flex flex-col"
            data-testid="mobile-menu"
          >
            <div className="h-20 px-6 flex items-center justify-between">
              <Logo compact />
              <button onClick={() => setOpen(false)} aria-label="Close menu" data-testid="mobile-menu-close" className="text-[#F7F5F0]">
                <X size={28} />
              </button>
            </div>
            <div className="flex-1 flex flex-col items-center justify-center gap-8">
              {links.map((l, i) => (
                <motion.button
                  key={l.id}
                  initial={{ y: 30, opacity: 0 }}
                  animate={{ y: 0, opacity: 1 }}
                  transition={{ delay: 0.1 + i * 0.08, duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
                  onClick={() => go(l.id)}
                  data-testid={`mobile-${l.testid}`}
                  className="font-display text-4xl text-[#F7F5F0] hover:text-[#D4AF37] transition-colors duration-300"
                >
                  {l.label}
                </motion.button>
              ))}
              <div className="flex items-center gap-2 mt-6 border border-[rgba(212,175,55,0.25)] rounded-full px-1 py-1">
                {Object.keys(languageNames).map((code) => (
                  <button
                    key={code}
                    onClick={() => changeLang(code)}
                    data-testid={`mobile-lang-switch-${code}`}
                    className={`font-label text-xs tracking-[0.15em] px-3 py-1.5 rounded-full transition-all duration-300 ${
                      lang === code ? "bg-[#D4AF37] text-[#050B14] font-semibold" : "text-slate-400"
                    }`}
                  >
                    {languageNames[code]}
                  </button>
                ))}
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};
